import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import api from '../utils/axiosConfig';
import { setUserType } from '../store/authSlice';

// Used inside DevicePage's identity-keyed content. Reads may be repeated, commands may not.
export default function useDeviceData(unit) {
    const dispatch = useDispatch();
    const [result, setResult] = useState({ status: 'loading' });
    const saved = useRef(null);
    const pending = useRef(null);
    const alive = useRef(false);
    const reload = useCallback(() => {
        if (!alive.current || !unit?.UnitSerNum) return Promise.resolve(null);
        if (pending.current) return pending.current.promise;
        const controller = new AbortController();
        const promise = api.post('/api/unitData', saved.current || {}, {
            params: { unit: unit.UnitSerNum }, signal: controller.signal
        }).then(({ data }) => {
            if (!alive.current || controller.signal.aborted) return null;
            saved.current = data;
            if (data.userMap) dispatch(setUserType({ userType: data.userMap }));
            setResult({ status: 'ready', data });
            return data;
        }).catch(error => {
            if (!alive.current || controller.signal.aborted) return null;
            setResult({ status: saved.current ? 'ready' : 'error', data: saved.current, refreshError: true });
            throw error;
        }).finally(() => {
            if (pending.current?.controller === controller) pending.current = null;
        });
        pending.current = { controller, promise };
        return promise;
    }, [unit?.UnitSerNum, dispatch]);
    useEffect(() => {
        alive.current = true;
        reload().catch(() => {});
        return () => {
            alive.current = false;
            pending.current?.controller.abort();
            pending.current = null;
        };
    }, [reload]);
    const retry = () => {
        setResult({ status: 'loading' });
        reload().catch(() => {});
    };
    return { ...result, reload, retry };
}
